import { InstantConfigService, Tcf, Usp, utils } from '@ad-engine/core';
import { Injectable } from '@wikia/dependency-injection';
import { Bidders } from '../../../../ad-bidders';
import { videoDisplayTakeoverSynchronizer } from './video-display-takeover-synchronizer';

export interface VastResponseData {
	xml?: string;
	lineItemId?: string | null;
	creativeId?: string | null;
}

/**
 * Fires a tagless request to speed up video Ads recognition before player does it.
 * Then uses videoDisplayTakeoverSynchronizer to synchronize.
 */
@Injectable()
export class VastTaglessRequest {
	private readonly timeout: number;

	constructor(
		private fetchTimeout: utils.FetchTimeout,
		instantConfig: InstantConfigService,
		private bidders: Bidders,
		private tcf: Tcf,
		private usp: Usp,
	) {
		this.timeout = instantConfig.get('icVastRequestTimeout', 500);
	}

	public async buildTaglessVideoRequest(isTagless = true): Promise<string> {
		const aspectRatio = 16 / 9;
		const slotName = 'featured';
		const position = 'preroll';

		const biddersTargeting = await this.bidders.getBidParameters(slotName);
		const defaultVastOptions: Partial<utils.VastOptions> = {
			vpos: position,
			targeting: biddersTargeting,
			isTagless,
		};

		return utils.buildVastUrl(
			aspectRatio,
			slotName,
			await this.extendOptionsBasedOnPrivacyData(defaultVastOptions),
		);
	}

	private async extendOptionsBasedOnPrivacyData(vastOptions: Partial<utils.VastOptions>) {
		if (this.tcf.exists) {
			const signalData = await this.tcf.getTCData();
			vastOptions = {
				...vastOptions,
				gdpr_consent: signalData.tcString,
			};
		}

		if (this.usp.exists) {
			const signalData = await this.usp.getSignalData();
			vastOptions = {
				...vastOptions,
				us_privacy: signalData.uspString,
			};
		}

		return vastOptions;
	}

	public async getVast(): Promise<VastResponseData | undefined> {
		try {
			const vastUrl = await this.buildTaglessVideoRequest();
			const response = await this.fetchTimeout.fetch(vastUrl, this.timeout);
			const textXml = await response.text();
			const vastData = this.getFirstAdFromTaglessResponse(textXml);
			videoDisplayTakeoverSynchronizer.resolve(vastData.lineItemId, vastData.creativeId);
			return vastData;
		} catch {
			videoDisplayTakeoverSynchronizer.resolve();
		}

		return undefined;
	}

	private getFirstAdFromTaglessResponse(textXml: string): VastResponseData {
		const parser = new DOMParser();
		const xmlDocument = parser.parseFromString(textXml, 'text/xml');
		const adElements = xmlDocument.getElementsByTagName('Ad');
		const creativeElements = xmlDocument.getElementsByTagName('Creative');

		return {
			xml: adElements.length > 0 ? textXml : undefined,
			lineItemId: adElements[0]?.getAttribute('id'),
			creativeId: creativeElements[0]?.getAttribute('id'),
		};
	}
}
